import { useCallback, useEffect } from "react";
import { isDirectWSSupported } from "./direct-ws/constants";
import { useExchangeOrderBook } from "./direct-ws/useExchangeOrderBook";
import { useOrderBookSSE } from "./useOrderBookSSE";
import { OrderBook } from "@/components/features/trade/OrderBook/types";
import { useOrderBookStore } from "@/store/orderBookStore";

/**
 * This hook is used to get the orderbook in real time from the server
 * It also provides a function to aggregate the orderbook by precision.
 * It also stores the orderbook in the store so that it can be used by other components.
 * ⚠️ It should be mounted only once
 *
 * For exchanges in `DIRECT_WS_SUPPORTED_EXCHANGES`, the data comes from a direct
 * WebSocket connection to the exchange. For others, it falls back to the Kenobi
 * SSE proxy.
 *
 * @param pairSymbol - The pair symbol to get the orderbook for.
 * @param exchangeId - The exchange id to get the orderbook for.
 * @returns The orderbook and the function to aggregate the orderbook by precision.
 */
export const useOrderBookStream = (pairSymbol?: string, exchangeId?: string) => {
  const useDirectWs = !!(exchangeId && isDirectWSSupported(exchangeId));

  // Direct exchange WS — active only when supported. `useExchangeOrderBook` already
  // calls every per-exchange hook with `enabled` gated on the matching id, so passing
  // `enabled: false` here makes the whole tree idle.
  const directResult = useExchangeOrderBook({
    exchangeId: exchangeId ?? "",
    pairSymbol: pairSymbol ?? "",
    externalMarketId: pairSymbol ?? "",
    enabled: useDirectWs,
  });

  // Kenobi SSE — active only for unsupported exchanges. Passing undefined keeps it idle.
  const sse = useOrderBookSSE({
    pairSymbol: useDirectWs ? undefined : pairSymbol,
    exchangeId: useDirectWs ? undefined : exchangeId,
  });

  const orderBookData = useDirectWs ? directResult?.orderBookData : sse.orderBookData;
  const isConnected = useDirectWs ? (directResult?.isConnected ?? false) : sse.isConnected;
  const error = useDirectWs ? (directResult?.error ?? null) : sse.error;
  const isPairUnavailable = useDirectWs ? false : sse.isPairUnavailable;
  const setOrderBook = useOrderBookStore((state) => state.setOrderBook);
  const orderBook = useOrderBookStore((state) => state.orderBook);

  useEffect(() => {
    if (orderBookData && pairSymbol && exchangeId) {
      setOrderBook({
        asks: orderBookData.asks,
        bids: orderBookData.bids,
        timestamp: orderBookData.timestamp,
        exchangeId,
        pairSymbol,
      });
    }
  }, [orderBookData, pairSymbol, exchangeId, setOrderBook]);

  // Function to aggregate orderbook data by precision
  const getAggregatedOrderBook = useCallback(
    (precision: number): OrderBook | null => {
      if (!orderBook || !pairSymbol || !exchangeId) return null;

      // Helper function to aggregate levels by precision
      // Uses integer scaling to avoid floating-point inaccuracies in financial calculations
      const aggregateByPrecision = (levels: [number, number][]): [number, number][] => {
        const aggregated = new Map<number, number>();
        const scale = 1 / precision;

        levels.forEach(([price, amount]) => {
          // Round price using integer scaling for precision
          const roundedPrice = Math.round(price * scale) / scale;

          // Aggregate amounts for the same rounded price
          const existingAmount = aggregated.get(roundedPrice) || 0;
          aggregated.set(roundedPrice, existingAmount + amount);
        });

        // Convert back to array and sort
        return Array.from(aggregated.entries()).sort((a, b) => a[0] - b[0]);
      };

      // Aggregate bids and asks
      const aggregatedBids = aggregateByPrecision(orderBook.bids);
      const aggregatedAsks = aggregateByPrecision(orderBook.asks);

      // Sort bids descending (highest price first)
      aggregatedBids.sort((a, b) => b[0] - a[0]);

      // Sort asks ascending (lowest price first)
      aggregatedAsks.sort((a, b) => a[0] - b[0]);

      return {
        bids: aggregatedBids,
        asks: aggregatedAsks,
        timestamp: orderBook.timestamp,
      };
    },
    [orderBook, pairSymbol, exchangeId],
  );

  // cleanup the orderbook when the component unmounts since the orderbook
  // won't be updated anymore
  useEffect(() => {
    return () => {
      setOrderBook(null);
    };
  }, []);

  return {
    orderBook: orderBook ?? undefined, // Return raw orderbook for precision calculation
    getAggregatedOrderBook,
    isConnected: !!(pairSymbol && exchangeId) && isConnected,
    error,
    isPairUnavailable,
  };
};
